import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { importantContacts, adminContact } from '../data/mock';
import { getServices } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  GraduationCap, Hospital, Landmark, Store, Church, Building2, Phone, 
  Users, Shield, UserCheck, Heart, Loader2
} from 'lucide-react';

const ServicesPage = () => {
  const { getText, language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'school': return GraduationCap;
      case 'hospital': return Hospital;
      case 'atm': return Landmark;
      case 'shop': return Store;
      case 'religious': return Church;
      case 'government': return Building2;
      case 'ngo': return Heart;
      default: return Building2;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      school: { en: 'Education', pa: 'ਸਿੱਖਿਆ' },
      hospital: { en: 'Healthcare', pa: 'ਸਿਹਤ' },
      atm: { en: 'Banking', pa: 'ਬੈਂਕਿੰਗ' },
      shop: { en: 'Services', pa: 'ਸੇਵਾਵਾਂ' },
      religious: { en: 'Religious Places', pa: 'ਧਾਰਮਿਕ ਸਥਾਨ' },
      government: { en: 'Government', pa: 'ਸਰਕਾਰੀ' },
      ngo: { en: 'NGO / Social', pa: 'ਐਨਜੀਓ / ਸਮਾਜਿਕ' }
    };
    return labels[type] ? labels[type][language] : type;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'school': return 'from-blue-500 to-cyan-500 bg-blue-50 text-blue-600';
      case 'hospital': return 'from-red-500 to-pink-500 bg-red-50 text-red-600';
      case 'atm': return 'from-emerald-500 to-teal-500 bg-emerald-50 text-emerald-600';
      case 'shop': return 'from-purple-500 to-indigo-500 bg-purple-50 text-purple-600';
      case 'religious': return 'from-amber-500 to-orange-500 bg-amber-50 text-amber-600';
      case 'government': return 'from-gray-500 to-slate-500 bg-gray-50 text-gray-600';
      case 'ngo': return 'from-pink-500 to-rose-500 bg-pink-50 text-pink-600';
      default: return 'from-gray-500 to-slate-500 bg-gray-50 text-gray-600';
    }
  };

  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.type]) acc[service.type] = [];
    acc[service.type].push(service);
    return acc;
  }, {});

  const getServiceName = (service) => {
    return language === 'pa' ? (service.name_pa || service.name_en) : service.name_en;
  };

  const getServiceDetails = (service) => {
    if (!service.details_en && !service.details_pa) return null;
    return language === 'pa' ? (service.details_pa || service.details_en) : service.details_en;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {language === 'pa' ? 'ਸਥਾਨਕ ਸੇਵਾਵਾਂ' : 'Local Services'}
            </h1>
            <p className="text-amber-100">
              {language === 'pa' ? 'ਪਿੰਡ ਵਿੱਚ ਉਪਲਬਧ ਸੇਵਾਵਾਂ' : 'Services available in the village'}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Important Contacts Section */}
        <Card className="border-0 shadow-lg mb-10 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <CardTitle className="flex items-center gap-2 text-white">
              <Users className="h-5 w-5" />
              {language === 'pa' ? 'ਮਹੱਤਵਪੂਰਨ ਸੰਪਰਕ' : 'Important Contacts'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {importantContacts.map((contact) => (
                <div key={contact.id} className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <UserCheck className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-amber-600 font-medium">{getText(contact.title)}</p>
                      <p className="font-semibold text-gray-900">{contact.name}</p>
                    </div>
                  </div>
                  <a 
                    href={`tel:${contact.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-amber-600 transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {contact.phone}
                  </a>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Services by Category */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          </div>
        ) : (
          Object.entries(groupedServices).map(([type, typeServices]) => {
            const Icon = getIcon(type);
            const colors = getTypeColor(type);
            const [gradientColors, bgColor, textColor] = colors.split(' ');
            
            return (
              <div key={type} className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradientColors} flex items-center justify-center`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">{getTypeLabel(type)}</h2>
                </div>
                
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {typeServices.map((service) => (
                    <Card key={service.id} className="border-0 shadow-md hover:shadow-lg transition-all duration-300">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
                            <Icon className={`h-6 w-6 ${textColor}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">
                              {getServiceName(service)}
                            </h3>
                            {getServiceDetails(service) && (
                              <p className="text-xs text-gray-500 mt-1">
                                {getServiceDetails(service)}
                              </p>
                            )}
                            {service.phone && (
                              <a 
                                href={`tel:${service.phone.replace(/\s/g, '')}`}
                                className="flex items-center gap-1 text-sm text-gray-500 hover:text-amber-600 mt-2 transition-colors"
                              >
                                <Phone className="h-3 w-3" />
                                {service.phone}
                              </a>
                            )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })
        )}

        {/* Website Admin Contact */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-gray-800 to-gray-900 text-white mt-10">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-amber-500 flex items-center justify-center">
                  <Shield className="h-7 w-7 text-white" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">{getText(adminContact.role)}</p>
                  <h3 className="text-xl font-bold">{adminContact.name}</h3>
                </div>
              </div>
              <a 
                href={`tel:${adminContact.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors font-medium"
              >
                <Phone className="h-5 w-5" />
                {adminContact.phone}
              </a>
            </div>
            <p className="text-gray-400 text-sm mt-4 text-center md:text-left">
              {language === 'pa' 
                ? 'ਵੈੱਬਸਾਈਟ ਨਾਲ ਸੰਬੰਧਿਤ ਕਿਸੇ ਵੀ ਸਵਾਲ ਲਈ ਸੰਪਰਕ ਕਰੋ'
                : 'Contact for any website related queries or to add your information'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ServicesPage;
